import { harvestWeightUnitKey } from "@common/helpers/harvestWeightUnit";
import type { IWeightFormatter } from "@common/helpers/WeightFormatter";
import type { TaskModalTask } from "@common/types";
import type { CropValue } from "@common/types/CropValue";
import type { CurrencyPreference } from "@common/types/User";
import type { CompleteTaskParams } from "@shared/api";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { Fragment } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "components/core/Button";
import { Divider } from "components/core/Divider";
import { ErrorBoundary } from "components/core/ErrorBoundary";
import { Flex } from "components/core/Flex";
import {
  getCachedWeightUnit,
  persistWeightUnit
} from "helpers/units/weight/getCachedWeightUnit";
import { useIsFeatureEnabled } from "hooks";
import { useSuspenseCropValuesQuery } from "queries/useCropValuesQuery";

import FinalHarvestingSection from "./FinalHarvestingSection";
import HarvestRecordSection from "./HarvestRecordSection";
import InventorySection from "./InventorySection";
import OverdueSection from "./OverdueSection";
import type { CompletionFormValues } from "./types";

type Props = {
  task: Pick<TaskModalTask, "id" | "name" | "taskType" | "cropId" | "gardenCropId">;
  isOverdue: boolean;
  isHarvesting: boolean;
  showInventory: boolean;
  showHarvestRecord: boolean;
  isSubmitting: boolean;
  selectedDate?: string;
  currencyPrefix: string;
  currencyPreference: CurrencyPreference;
  unitPreference: "imperial" | "metric";
  formatter: IWeightFormatter;
  onConfirm: (data: Omit<CompleteTaskParams, "id">) => void;
  onCancel: () => void;
};

type FieldsProps = Props & {
  cropValue: CropValue | null;
};

const CompletionFormFields = ({
  task,
  isOverdue,
  isHarvesting,
  showInventory,
  showHarvestRecord,
  isSubmitting,
  selectedDate,
  currencyPrefix,
  currencyPreference,
  unitPreference,
  formatter,
  onConfirm,
  onCancel,
  cropValue
}: FieldsProps) => {
  const isPerCrop = useIsFeatureEnabled("remember_harvest_unit_per_crop");
  const cacheKey = harvestWeightUnitKey(isPerCrop ? task.cropId : undefined);
  const cachedUnit = getCachedWeightUnit(cacheKey, unitPreference);
  const handleWeightUnitChange = (unit: string) => persistWeightUnit(cacheKey, unit);

  const harvestValuePerUnit = cropValue
    ? parseFloat(
        (cropValue.valuePerGram * formatter.fromDisplayUnit(1, cachedUnit)).toFixed(2)
      )
    : null;

  const form = useForm<CompletionFormValues>({
    defaultValues: {
      isFinal: null,
      overdueAcknowledged: false,
      completedAt: selectedDate,
      consumedItems: [],
      inventoryPage: 1,
      inventoryDisabled: false,
      harvestDisabled: false,
      harvestAmount: null,
      harvestNotes: "",
      harvestUnit: cachedUnit,
      harvestValuePerUnit,
      harvestValueUnit: cachedUnit,
      saveAsDefault: false
    }
  });

  const isFinal = form.watch("isFinal");
  const overdueAcknowledged = form.watch("overdueAcknowledged");

  const isConfirmDisabled =
    isSubmitting ||
    (isOverdue && !overdueAcknowledged) ||
    (isHarvesting && isFinal === null);

  const handleSubmit = (values: CompletionFormValues) => {
    let harvestRecord: CompleteTaskParams["harvestRecord"];

    if (values.harvestAmount) {
      const amountGrams = formatter.fromDisplayUnit(
        values.harvestAmount,
        values.harvestUnit
      );
      const valuePerGram =
        values.harvestValuePerUnit != null
          ? values.harvestValuePerUnit /
            formatter.fromDisplayUnit(1, values.harvestValueUnit)
          : undefined;

      harvestRecord = {
        amountGrams,
        notes: values.harvestNotes || undefined,
        valuePerGram,
        saveAsDefault: valuePerGram != null ? values.saveAsDefault : undefined,
        currency: currencyPreference
      };
    }

    onConfirm({
      completedAt: values.completedAt,
      final: isHarvesting ? !!values.isFinal : true,
      consumedItems: values.inventoryDisabled ? [] : values.consumedItems,
      harvestRecord: values.harvestDisabled ? undefined : harvestRecord
    });
  };

  const sections = [
    isOverdue && <OverdueSection key="overdue" taskType={task.taskType} />,
    isHarvesting && <FinalHarvestingSection key="final-harvesting" />,
    showInventory && <InventorySection key="inventory" task={task} />,
    showHarvestRecord && (
      <HarvestRecordSection
        key="harvest-record"
        currencyPrefix={currencyPrefix}
        formatter={formatter}
        onWeightUnitChange={handleWeightUnitChange}
      />
    )
  ].filter((section) => section !== false);

  return (
    <FormProvider {...form}>
      <Flex fullWidth direction="col" gap={2}>
        {sections.map((section, index) => (
          <Fragment key={section.key}>
            {index > 0 && <Divider spacing="xs" />}
            {section}
          </Fragment>
        ))}
      </Flex>
      <Flex className="mt-4" gap={2} justify="end">
        <Button data-testid="modal-cancel" disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          data-testid="modal-confirm"
          disabled={isConfirmDisabled}
          variant="solid"
          onClick={form.handleSubmit(handleSubmit)}
        >
          <Check size="1em" />
          Complete Task
        </Button>
      </Flex>
    </FormProvider>
  );
};

const CompletionFormWithCropValue = (props: Props) => {
  const { data: cropValue } = useSuspenseCropValuesQuery({
    queryParams: { gardenCropId: props.task.gardenCropId },
    select: (data) => data.items[0] ?? null
  });

  return <CompletionFormFields {...props} cropValue={cropValue} />;
};

const CompletionForm = (props: Props) => {
  const queryClient = useQueryClient();

  if (!props.showHarvestRecord)
    return <CompletionFormFields {...props} cropValue={null} />;

  return (
    <ErrorBoundary
      fallback={<CompletionFormFields {...props} cropValue={null} />}
      onError={() =>
        queryClient.removeQueries({
          exact: true,
          queryKey: ["cropValues", { gardenCropId: props.task.gardenCropId }]
        })
      }
    >
      <CompletionFormWithCropValue {...props} />
    </ErrorBoundary>
  );
};

export default CompletionForm;
